import { Controller } from "@hotwired/stimulus";

// Usage: data-controller="decline-application-modal"
// Handles multi-step decline application modal
export default class extends Controller {
  static targets = [
    "stepIndicator",
    "stepPanel",
    "externalComment",
    "reasonRadio",
    "otherReasonContainer",
    "internalComment",
    "nextStepBtn",
    "submitBtn",
    "step1Buttons",
    "step2Buttons"
  ];

  connect() {
    this.currentStep = 1;
    this._updateStepDisplay();
    
    // Listen for Bootstrap modal hidden event to reset the form
    this.element.addEventListener('hidden.bs.modal', this._handleModalHidden.bind(this));
  }

  disconnect() {
    this.element.removeEventListener('hidden.bs.modal', this._handleModalHidden.bind(this));
  }

  _handleModalHidden() {
    this.reset();
  }

  reset() {
    // Reset to step 1
    this.currentStep = 1;
    this._updateStepDisplay();

    // Clear external comment
    if (this.hasExternalCommentTarget) {
      this.externalCommentTarget.value = '';
    }

    // Clear all radio buttons
    this.reasonRadioTargets.forEach(radio => {
      radio.checked = false;
    });

    // Hide other reason container and clear its content
    if (this.hasOtherReasonContainerTarget) {
      this.otherReasonContainerTarget.style.display = 'none';
    }
    if (this.hasInternalCommentTarget) {
      this.internalCommentTarget.value = '';
    }

    // Reset button states
    if (this.hasNextStepBtnTarget) {
      this.nextStepBtnTarget.disabled = true;
    }
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true;
    }
  }

  validateStep1() {
    const externalComment = this.externalCommentTarget.value.trim();
    this.nextStepBtnTarget.disabled = externalComment.length === 0;
  }

  goToStep2() {
    this.currentStep = 2;
    this._updateStepDisplay();
    this.validateStep2();
  }

  goToStep1() {
    this.currentStep = 1;
    this._updateStepDisplay();
  }

  goToStep(event) {
    const step = parseInt(event.currentTarget.dataset.step);
    
    // Can always go back to step 1
    if (step === 1) {
      this.goToStep1();
      return;
    }
    
    // Can only go to step 2 if step 1 is valid (external comment is filled)
    if (step === 2) {
      const externalComment = this.externalCommentTarget.value.trim();
      if (externalComment.length > 0) {
        this.goToStep2();
      }
    }
  }

  onReasonChange(event) {
    const selectedValue = event.target.value;
    
    if (selectedValue === 'other') {
      this.otherReasonContainerTarget.style.display = 'block';
    } else {
      this.otherReasonContainerTarget.style.display = 'none';
      // Clear the internal comment when switching away from "other"
      if (this.hasInternalCommentTarget) {
        this.internalCommentTarget.value = '';
      }
    }
    
    this.validateStep2();
  }

  validateStep2() {
    const selectedReason = this.reasonRadioTargets.find(radio => radio.checked);
    
    if (!selectedReason) {
      this.submitBtnTarget.disabled = true;
      return;
    }

    // If "other" is selected, require the internal comment
    if (selectedReason.value === 'other') {
      const internalComment = this.internalCommentTarget.value.trim();
      this.submitBtnTarget.disabled = internalComment.length === 0;
    } else {
      this.submitBtnTarget.disabled = false;
    }
  }

  _updateStepDisplay() {
    // Update step indicators
    this.stepIndicatorTargets.forEach(indicator => {
      const step = parseInt(indicator.dataset.step);
      indicator.classList.toggle('active', step === this.currentStep);
    });

    // Update step panels
    this.stepPanelTargets.forEach(panel => {
      const step = parseInt(panel.dataset.step);
      panel.classList.toggle('active', step === this.currentStep);
    });

    // Update button visibility
    this.step1ButtonsTarget.style.display = this.currentStep === 1 ? 'flex' : 'none';
    this.step2ButtonsTarget.style.display = this.currentStep === 2 ? 'flex' : 'none';
  }
}
